/**
 * Fælles dato- og tekstformatering. Alle datoer kommer som UTC fra API-et
 * (med "Z" til sidst, se backendens Configuration/DanishTime.cs).
 *
 * Tidszonen er bundet til Europe/Copenhagen frem for maskinens egen: en bærbar
 * med forkert tidszone, en server i udlandet eller et TV-dashboard skal vise
 * samme klokkeslæt som montørens ur.
 */
const TIME_ZONE = 'Europe/Copenhagen'

const dateFormatter = new Intl.DateTimeFormat('da-DK', {
  day: '2-digit', month: '2-digit', year: 'numeric', timeZone: TIME_ZONE
})
const shortDateFormatter = new Intl.DateTimeFormat('da-DK', {
  day: '2-digit', month: '2-digit', timeZone: TIME_ZONE
})
const timeFormatter = new Intl.DateTimeFormat('da-DK', {
  hour: '2-digit', minute: '2-digit', timeZone: TIME_ZONE
})

export function formatDate(value?: string | null) {
  if (!value) return '-'
  return dateFormatter.format(new Date(value))
}

export function formatShortDate(value?: string | null) {
  if (!value) return '-'
  return shortDateFormatter.format(new Date(value))
}

export function formatDateTime(value?: string | null) {
  if (!value) return '-'
  const date = new Date(value)
  return `${dateFormatter.format(date)} ${timeFormatter.format(date)}`
}

export function formatTime(value?: string | null) {
  if (!value) return '-'
  return timeFormatter.format(new Date(value))
}

/** "for 5 min siden", "i går" osv. Bruges i aktivitetsfeeds. */
export function formatRelative(value?: string | null) {
  if (!value) return ''

  const diffMs = Date.now() - new Date(value).getTime()
  const minutes = Math.round(diffMs / 60000)

  if (minutes < 1) return 'lige nu'
  if (minutes < 60) return `for ${minutes} min siden`

  const hours = Math.round(minutes / 60)
  if (hours < 24) return `for ${hours} ${hours === 1 ? 'time' : 'timer'} siden`

  const days = Math.round(hours / 24)
  if (days === 1) return 'i går'
  if (days < 7) return `for ${days} dage siden`

  return formatDate(value)
}

/** Antal dage til deadline. Negativt tal betyder overskredet. */
export function daysUntil(value?: string | null) {
  if (!value) return null
  const target = new Date(value)
  const now = new Date()
  return Math.ceil((target.getTime() - now.getTime()) / 86_400_000)
}

/** Kort tekst om deadline, f.eks. "om 3 dage" eller "2 dage forsinket". */
export function deadlineLabel(value?: string | null) {
  const days = daysUntil(value)
  if (days === null) return ''
  if (days < 0) return `${Math.abs(days)} ${Math.abs(days) === 1 ? 'dag' : 'dage'} forsinket`
  if (days === 0) return 'i dag'
  if (days === 1) return 'i morgen'
  return `om ${days} dage`
}

export const weekdayNames = ['Søndag', 'Mandag', 'Tirsdag', 'Onsdag', 'Torsdag', 'Fredag', 'Lørdag']

/** Gør en hex-farve gennemsigtig, så den kan bruges som baggrund bag samme farve tekst. */
export function withAlpha(hex: string, alpha: number) {
  const match = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  if (!match) return hex
  const [, r, g, b] = match
  return `rgba(${parseInt(r, 16)}, ${parseInt(g, 16)}, ${parseInt(b, 16)}, ${alpha})`
}

/**
 * "Udloeber om 3 maaneder" - i ord, saa et tal paa 94 ikke skal regnes om i
 * hovedet. Eksporteret, fordi enhedens historik (beslutning 062) viser de samme
 * garantier og skal skrive det paa samme maade.
 */
export function describeExpiry(days: number) {
  if (days < 0) {
    const n = Math.abs(days)
    return n === 1 ? 'Udløb i går' : `Udløb for ${n} dage siden`
  }
  if (days === 0) return 'Udløber i dag'
  if (days === 1) return 'Udløber i morgen'
  if (days < 60) return `Udløber om ${days} dage`
  if (days < 365) return `Udløber om ${Math.round(days / 30)} måneder`

  const years = Math.round((days / 365) * 10) / 10
  return `Udløber om ${years.toString().replace('.', ',')} år`
}
